import React, { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getSessionAnalytics } from '../../services/api';import { useApiQuery } from '../../hooks/useApiQuery';

const PeakHours = () => {
  const { eventId } = useOutletContext();

  // ✅ React Query: Fetch sessions
  const {
  data: analyticsData = {},
  isLoading,
  isError,
} = useApiQuery(
  ['session-analytics', eventId],
  () => getSessionAnalytics(eventId),
  {
    enabled: Boolean(eventId),
    staleTime: 60 * 1000,
    onError: (error) => {
      console.error(
        'Failed to fetch peak hours analytics:',
        error
      );
    },
  }
);

  // ✅ Process peak hours data (memoized for performance)
const peakHours = useMemo(() => {
  const backendPeakHours =
    analyticsData?.peakHours || [];

  // Always keep all 24 hours
  // even when backend returns only
  // hours that have activity.

  const hourMap = new Map(
    backendPeakHours.map(item => [
      Number(item.hour),
      Number(item.count) || 0,
    ])
  );

  return Array.from(
    { length: 24 },
    (_, hour) => {
      const count =
        hourMap.get(hour) || 0;

      return {
        hour,
        count,
        label: `${hour}:00`,
        isPeak: count > 0,
      };
    }
  );
}, [analyticsData]);

  // ✅ Calculate max count for gradient scaling
  const maxCount = useMemo(() => {
    return Math.max(...peakHours.map(h => h.count), 1);
  }, [peakHours]);

  // ✅ Find peak hours
  const peakHourInfo = useMemo(() => {
    const max = Math.max(...peakHours.map(h => h.count));
    const peakHoursList = peakHours.filter(h => h.count === max && max > 0);
    return {
      max,
      count: peakHoursList.length,
      hours: peakHoursList.map(h => h.hour),
    };
  }, [peakHours]);

  // ✅ Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-[#1471b9] border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-[#708090] text-sm font-['Barlow']">Loading peak hours data...</p>
        </div>
      </div>
    );
  }

  // ✅ Error state
  if (isError) {
    return (
      <div className="text-center py-10 text-[#708090]">
        <p className="text-red-500">Failed to load peak hours data</p>
        <p className="text-sm mt-2">Please refresh the page or try again later.</p>
      </div>
    );
  }

  // ✅ No data state
  const hasData = peakHours.some(h => h.count > 0);

  if (!hasData) {
    return (
      <div>
        <h2 className="text-2xl font-bold mb-6 text-[#2b3445]">Peak Hours</h2>
        <div className="flex flex-col items-center justify-center py-16 bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)]">
          <svg
            className="w-16 h-16 mb-4 text-[#dae1e7]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-[#708090] text-sm">No scan data available for this event</p>
          <p className="text-xs text-[#708090] mt-1">Check back after attendees start checking in</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 text-[#2b3445]">Peak Hours</h2>
      
      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-4 text-center">
          <p className="text-xs font-semibold text-[#708090] uppercase tracking-wide">Total Scans</p>
          <p className="text-2xl font-bold text-[#2b3445] mt-1">
            {peakHours.reduce((sum, h) => sum + h.count, 0)}
          </p>
        </div>
        <div className="bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-4 text-center">
          <p className="text-xs font-semibold text-[#708090] uppercase tracking-wide">Peak Hour(s)</p>
          <p className="text-2xl font-bold text-[#1471b9] mt-1">
            {peakHourInfo.hours.map(h => `${h}:00`).join(', ')}
          </p>
        </div>
        <div className="bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-4 text-center">
          <p className="text-xs font-semibold text-[#708090] uppercase tracking-wide">Max Scans in an Hour</p>
          <p className="text-2xl font-bold text-green-600 mt-1">{peakHourInfo.max}</p>
        </div>
      </div>

      {/* Peak Hours Heatmap */}
      <div className="bg-white p-4 rounded-[15px] shadow-sm border border-[#dae1e7]">
        <h3 className="text-lg font-semibold mb-4 text-[#2b3445]">Scans per Hour (24h)</h3>
        
        <div className="grid grid-cols-12 gap-1 sm:gap-2">
          {peakHours.map((item, idx) => {
            const intensity = maxCount > 0 ? item.count / maxCount : 0;
            const color = intensity > 0.7 ? '#1a56db' : 
                         intensity > 0.4 ? '#3b82f6' : 
                         intensity > 0.2 ? '#60a5fa' : 
                         intensity > 0 ? '#93bbfc' : '#e5e7eb';
            const textColor = intensity > 0.5 ? 'text-white' : 'text-gray-700';
            
            return (
              <div key={idx} className="text-center">
                <div 
                  className="h-20 w-full rounded-md flex items-center justify-center font-bold text-xs transition-all duration-200 hover:scale-110"
                  style={{ backgroundColor: color }}
                >
                  <span className={textColor}>{item.count}</span>
                </div>
                <div className="text-xs mt-1 text-gray-600 font-medium">
                  {idx}:00
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-6 flex items-center justify-center gap-4 text-xs text-gray-500">
          <span>Low</span>
          <div className="flex gap-0.5">
            <div className="w-6 h-3 rounded-sm" style={{ backgroundColor: '#e5e7eb' }}></div>
            <div className="w-6 h-3 rounded-sm" style={{ backgroundColor: '#93bbfc' }}></div>
            <div className="w-6 h-3 rounded-sm" style={{ backgroundColor: '#60a5fa' }}></div>
            <div className="w-6 h-3 rounded-sm" style={{ backgroundColor: '#3b82f6' }}></div>
            <div className="w-6 h-3 rounded-sm" style={{ backgroundColor: '#1a56db' }}></div>
          </div>
          <span>High</span>
        </div>
      </div>
    </div>
  );
};

export default PeakHours;